import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';

import {
  eligibleQuery,
  preferencesQuery,
  setCrewOrder,
  type CrewOrder,
  type Eligible,
  type WorkContext,
} from '../api/abilities.ts';
import { Button } from './button.tsx';

// CB-REQ CRW-03 at v1.9107, crew part 4E-3 (CB-CONV §12): who could do this work, as one shared
// piece — the shift popover's Offer list, the shift form's people, and adding people to an offer of
// places. It reads the eligible route, which filters nobody, and filters here: on from the work —
// free, its role, ticketed and inducted — each of which can be unticked, plus skills, tickets,
// employment type, class and tier by hand. Those who don't fit are counted, and listed with why under
// Show everyone. A filter, never a refusal: CMP-07 and CRW-12 still decide at commitment.

export interface Filters {
  free: boolean;
  role: boolean;
  compliant: boolean;
  skillId: string;
  ticketId: string;
  employmentTypeId: string;
  crewClass: string;
  /** The worst tier wanted, or '' for any. */
  tier: string;
}

const CLASS: Record<string, string> = {
  employee: 'Employee',
  contractor: 'Contractor',
  subcontractor: 'Subcontractor',
};

/** A time on the work's own clock (CB-CONV §3.2: a formatter never defaults the zone). */
const when = (instant: string, zone: string) =>
  new Intl.DateTimeFormat('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: zone,
  }).format(new Date(instant));

/** Why someone doesn't fit the filters; none, and they do. */
export function reasons(e: Eligible, f: Filters, roleName: string | null): string[] {
  const out: string[] = [];
  if (f.free) {
    for (const t of e.timeOff) {
      out.push(`${t.kind === 'unavailable' ? 'Unavailable' : 'On leave'} · ${t.label}`);
    }
    for (const c of e.clashes.filter((c) => c.state === 'accepted')) {
      out.push(c.bookedForJob ? `Clashes with J-${c.bookedForJob.number}` : 'Clashes with a shift');
    }
  }
  if (f.role && e.roleFit === 'other') out.push(`Not a ${roleName ?? 'fit for the role'}`);
  if (f.compliant && e.complianceGaps.length > 0) {
    out.push(`Needs ${e.complianceGaps.map((g) => g.name).join(', ')}`);
  }
  if (f.skillId && !e.skills.some((s) => s.id === f.skillId)) out.push('Lacks the skill');
  if (f.ticketId && !e.tickets.some((t) => t.id === f.ticketId)) out.push('Lacks the ticket');
  if (f.employmentTypeId && e.employmentType?.id !== f.employmentTypeId) {
    out.push(e.employmentType ? e.employmentType.name : 'No employment type');
  }
  if (f.crewClass && e.class !== f.crewClass) out.push(CLASS[e.class] ?? e.class);
  if (f.tier && (e.tier === null || e.tier === undefined || e.tier > Number(f.tier))) {
    out.push(e.tier ? `Tier ${e.tier}` : 'No tier');
  }
  return out;
}

/** The user's chosen order: tier then fewest hours, tier then name, or name. */
export function ordered(list: readonly Eligible[], order: CrewOrder): Eligible[] {
  const tier = (e: Eligible) => e.tier ?? 9;
  const name = (a: Eligible, b: Eligible) => a.crewMember.name.localeCompare(b.crewMember.name);
  return [...list].sort((a, b) =>
    order === 'name'
      ? name(a, b)
      : tier(a) - tier(b) ||
        (order === 'tier_hours' ? a.hoursThisWeek - b.hoursThisWeek : 0) ||
        name(a, b),
  );
}

const ORDERS: Record<CrewOrder, string> = {
  tier_hours: 'Tier, fewest hours',
  tier_name: 'Tier, name',
  name: 'Name',
};

/** One distinct list of a fact everyone carries, for its filter's choices. */
function choices(
  list: readonly Eligible[],
  pick: (e: Eligible) => Array<{ id: string; name: string }>,
) {
  const seen = new Map<string, string>();
  for (const e of list) for (const x of pick(e)) seen.set(x.id, x.name);
  return [...seen].sort((a, b) => a[1].localeCompare(b[1]));
}

export function CrewPicker({
  work,
  zone,
  roleName = null,
  exclude = [],
  chosen,
  onChoose,
  disabled = false,
  extra,
}: {
  work: WorkContext;
  /** The work's zone, for its times. */
  zone: string;
  /** The role's name, for "Not a Rigger". */
  roleName?: string | null;
  /** People not to list at all, e.g. already offered it. */
  exclude?: readonly string[];
  /** Several: the ticked; one: leave out and use onChoose. */
  chosen?: readonly string[];
  /** One: offer it to this person. Several: tick or untick them. */
  onChoose: (crewMemberId: string) => void;
  disabled?: boolean;
  /** More to say on a person's line, e.g. what they would have to sign. */
  extra?: (crewMemberId: string) => ReactNode;
}) {
  const queryClient = useQueryClient();
  const eligible = useQuery(eligibleQuery(work));
  const prefs = useQuery(preferencesQuery);
  const order = useMutation({
    mutationFn: setCrewOrder,
    onSuccess: (p) => queryClient.setQueryData(preferencesQuery.queryKey, p),
  });
  const [f, setF] = useState<Filters>({
    free: true,
    role: Boolean(work.roleId),
    compliant: true,
    skillId: '',
    ticketId: '',
    employmentTypeId: '',
    crewClass: '',
    tier: '',
  });
  const [everyone, setEveryone] = useState(false);
  const several = chosen !== undefined;
  const list = useMemo(
    () => (eligible.data ?? []).filter((e) => !exclude.includes(e.crewMember.id)),
    [eligible.data, exclude],
  );
  const sorted = ordered(list, prefs.data?.crewOrder ?? 'tier_hours');
  const assessed = sorted.map((e) => ({ e, why: reasons(e, f, roleName) }));
  const fit = assessed.filter((a) => a.why.length === 0);
  const rest = assessed.filter((a) => a.why.length > 0);
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) =>
    setF((now) => ({ ...now, [key]: value }));

  const select = (
    label: string,
    key: 'skillId' | 'ticketId' | 'employmentTypeId' | 'crewClass' | 'tier',
    options: Array<[string, string]>,
  ) =>
    options.length === 0 ? null : (
      <label className="flex items-center gap-1 text-xs">
        <span className="text-ink-soft">{label}</span>
        <select
          className="input w-auto py-1 text-xs"
          value={f[key]}
          onChange={(e) => set(key, e.currentTarget.value)}
        >
          <option value="">Any</option>
          {options.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </label>
    );

  const line = ({ e, why }: { e: Eligible; why: string[] }) => {
    const id = e.crewMember.id;
    const offered = e.clashes.filter((c) => c.state !== 'accepted');
    const tags = (
      <>
        {e.tier ? <span className="tag">T{e.tier}</span> : null}
        <span className="text-xs text-muted">{e.hoursThisWeek}h this week</span>
        {work.roleId && e.roleFit === 'not_set' ? (
          <span className="tag tag-warn">Roles not set</span>
        ) : null}
        {offered.map((c) => (
          <span key={c.shiftId} className="tag">
            Also offered {when(c.startsAt, zone)}
          </span>
        ))}
        {e.openOffers.length > 0 ? <span className="tag">Open offer then</span> : null}
        {!f.compliant && e.complianceGaps.length > 0 ? (
          <span className="tag tag-accent-2">
            Needs {e.complianceGaps.map((g) => g.name).join(', ')}
          </span>
        ) : null}
        {why.map((w) => (
          <span key={w} className="tag tag-accent-2">
            {w}
          </span>
        ))}
        {extra?.(id)}
      </>
    );
    return (
      <li key={id} className="flex flex-wrap items-center gap-2">
        {several ? (
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={chosen.includes(id)}
              disabled={disabled}
              onChange={() => onChoose(id)}
            />
            {e.crewMember.name}
          </label>
        ) : (
          <Button
            variant="quiet"
            className="small-btn"
            disabled={disabled}
            onClick={() => onChoose(id)}
          >
            {e.crewMember.name}
          </Button>
        )}
        {tags}
      </li>
    );
  };

  return (
    <div className="flex flex-col gap-2" aria-label="Crew">
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filters">
        {(
          [
            ['free', 'Free'],
            ...(work.roleId ? [['role', roleName ?? 'Role'] as const] : []),
            ['compliant', 'Ticketed and inducted'],
          ] as Array<readonly ['free' | 'role' | 'compliant', string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            className={`tag ${f[key] ? 'tag-accent' : ''}`}
            aria-pressed={f[key]}
            onClick={() => set(key, !f[key])}
          >
            {label}
          </button>
        ))}
        {select(
          'Skill',
          'skillId',
          choices(list, (e) => e.skills),
        )}
        {select(
          'Ticket',
          'ticketId',
          choices(list, (e) => e.tickets),
        )}
        {select(
          'Type',
          'employmentTypeId',
          choices(list, (e) => (e.employmentType ? [e.employmentType] : [])),
        )}
        {select('Class', 'crewClass', Object.entries(CLASS))}
        {select('Tier', 'tier', [
          ['1', '1'],
          ['2', '1 – 2'],
        ])}
        <label className="ml-auto flex items-center gap-1 text-xs">
          <span className="text-ink-soft">Order</span>
          <select
            className="input w-auto py-1 text-xs"
            value={prefs.data?.crewOrder ?? 'tier_hours'}
            disabled={order.isPending}
            onChange={(e) => order.mutate(e.currentTarget.value as CrewOrder)}
          >
            {(Object.keys(ORDERS) as CrewOrder[]).map((o) => (
              <option key={o} value={o}>
                {ORDERS[o]}
              </option>
            ))}
          </select>
        </label>
      </div>
      {eligible.isPending ? <p className="text-sm text-muted">Loading…</p> : null}
      {eligible.isError ? (
        <p role="alert" className="alert">
          {eligible.error.message}
        </p>
      ) : null}
      {eligible.data && list.length === 0 ? (
        <p className="text-sm text-ink-soft">
          Nobody is on the crew list yet. Add them under Crew.
        </p>
      ) : null}
      {eligible.data && list.length > 0 && fit.length === 0 ? (
        <p className="text-sm text-ink-soft">Nobody fits. Untick a filter, or show everyone.</p>
      ) : null}
      <ul className="flex max-h-64 flex-col gap-1 overflow-y-auto text-sm">
        {fit.map(line)}
        {everyone ? rest.map(line) : null}
      </ul>
      {rest.length > 0 ? (
        <button
          type="button"
          className="link self-start text-sm"
          aria-expanded={everyone}
          onClick={() => setEveryone(!everyone)}
        >
          {everyone ? 'Only those who fit' : `Show everyone (${rest.length} more)`}
        </button>
      ) : null}
    </div>
  );
}
